import type { ReactNode } from 'react';
import { Card } from '@/components/ui';

/**
 * AuthShell — centered 420px auth card with ambient sapphire wash + brand lockup.
 *
 * Used by every auth route (Login / Register / ResetPasswordRequest /
 * ResetPasswordConfirm). Lives in its own file to break the prior circular-import
 * smell (Register/Reset used to import `AuthShell` from `Login.tsx`).
 */
export function AuthShell({
  title,
  subtitle,
  children,
  footer,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  /** Optional cross-link row rendered below the card (e.g. "Нет аккаунта? Регистрация"). */
  footer?: ReactNode;
}) {
  return (
    <div className="min-h-screen auth-bg grid place-items-center p-4 relative">
      <div className="w-full max-w-[420px] relative z-10">
        {/* Brand lockup */}
        <div className="flex flex-col items-center mb-6">
          <div className="w-12 h-12 rounded-[var(--radius-md)] btn-gradient grid place-items-center text-[var(--accent-fg)] font-bold text-lg mb-3 shadow-[var(--shadow-md)]">
            N
          </div>
          <div className="text-center">
            <div className="font-semibold text-[var(--text)] text-lg leading-tight">Nexus</div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--accent-bright)] mt-0.5 font-semibold">
              Partner
            </div>
          </div>
        </div>

        <Card className="p-8">
          {(title || subtitle) && (
            <div className="mb-6">
              {title && (
                <h1 className="text-[22px] font-semibold tracking-tight text-[var(--text)] leading-tight">
                  {title}
                </h1>
              )}
              {subtitle && (
                <p className="text-sm text-[var(--text-muted)] mt-1.5">{subtitle}</p>
              )}
            </div>
          )}
          {children}
        </Card>

        {footer && (
          <div className="mt-5 text-center text-sm text-[var(--text-muted)]">{footer}</div>
        )}

        <p className="text-center text-xs text-[var(--text-quiet)] mt-5">
          © {new Date().getFullYear()} Nexus CRM
        </p>
      </div>
    </div>
  );
}
